/**
 * QuotesPage — the single consolidated "Quotes" tab.
 *
 * One entry point that folds the former separate menu items into segmented
 * sub-views (mounting the existing components as-is — no rewrites):
 *   • Quotes        — list of recent generated quotes (smart_quotes) + New Quote
 *   • Saved Templates — SavedQuoteTemplates (real quotes saved for reuse at Convert)
 *   • Backend Library (admin) — QuoteBackends (browse/import) + BackendBuilder (paste/author)
 *   • Templates     (admin) — QuoteTemplates (the playbook decision-rule editor)
 *
 * Old routes /quote-db, /backend-builder, /quote-templates redirect here with a
 * ?view= (and ?sub=) query param so bookmarks and in-app links keep working.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { usePermissions } from "../hooks/usePermissions";
import QuoteBackends from "./QuoteBackends";
import BackendBuilder from "./BackendBuilder";
import QuoteTemplates from "./QuoteTemplates";
import SavedQuoteTemplates from "./SavedQuoteTemplates";
import MaterialTakeoff from "./MaterialTakeoff";

const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", border: "#2a3140",
  lime: "#a5ce3a", text: "#e8eaf0", muted: "#8892a4",
};

function money(n) {
  if (n == null || isNaN(+n)) return null;
  return "$" + (+n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Best-effort quote total across the schema variants smart_quotes has carried.
function quoteTotal(q) {
  const j = q.quote_json;
  const cands = [q.total_estimated, q.total, q.grand_total, j?.totals?.grand_total, j?.total, j?.grandTotal, j?.recommended?.total,
    Array.isArray(j?.options) ? j.options.find(o => o?.recommended)?.total : null];
  const v = cands.find(x => x != null && !isNaN(+x));
  return v != null ? money(v) : null;
}

// Jobber request gid → web URL. jobber_request_id is a base64 GraphQL gid
// ("gid://Jobber/Request/31356378"); the web app uses the numeric tail.
function jobberRequestUrl(gid) {
  try {
    const decoded = atob(String(gid || ""));
    const m = decoded.match(/\/Request\/(\d+)$/);
    return m ? `https://secure.getjobber.com/work_requests/${m[1]}` : null;
  } catch { return null; }
}

function QuotesList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [fBy, setFBy] = useState("");
  const [fClient, setFClient] = useState("");
  const [fType, setFType] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [dFrom, setDFrom] = useState("");
  const [dTo, setDTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data, error: e } = await supabase
        .from("smart_quotes")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(500);
      if (e) throw e;
      const quotes = data || [];
      // Client name / address / author live on the assessment, not the quote — join them in.
      const aIds = [...new Set(quotes.map(q => q.assessment_id).filter(Boolean))];
      let aById = {};
      if (aIds.length) {
        const { data: assessments } = await supabase
          .from("smart_assessments")
          .select("id,client_name,address,created_by,service_type,jobber_request_id")
          .in("id", aIds);
        for (const a of (assessments || [])) aById[a.id] = a;
      }
      setRows(quotes.map(q => {
        const a = aById[q.assessment_id] || {};
        const status = q.workflow_state || (q.pushed_to_jobber_at ? "pushed" : "draft");
        const row = {
          ...q,
          _client_name: a.client_name || null,
          _address: a.address || null,
          _quoted_by: a.created_by || null,
          _service_type: q.service_type || a.service_type || null,
          _status: status,
          _jobber_url: jobberRequestUrl(a.jobber_request_id),
        };
        // One lowercase blob per row so the keyword search reaches everything —
        // client, title, address, and the full quote body (scope, materials, options…).
        row._search = [a.client_name, q.project_title, a.address, a.created_by, row._service_type, status,
          q.quote_json ? JSON.stringify(q.quote_json) : ""].filter(Boolean).join(" ").toLowerCase();
        return row;
      }));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Dropdown options come from the loaded rows themselves.
  const opts = useMemo(() => {
    const uniq = (arr) => [...new Set(arr.filter(Boolean))].sort((x, y) => String(x).localeCompare(String(y)));
    return {
      by: uniq(rows.map(r => r._quoted_by)),
      client: uniq(rows.map(r => r._client_name)),
      type: uniq(rows.map(r => r._service_type)),
      status: uniq(rows.map(r => r._status)),
    };
  }, [rows]);

  const filtered = useMemo(() => {
    const tokens = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter(r => {
      if (fBy && r._quoted_by !== fBy) return false;
      if (fClient && r._client_name !== fClient) return false;
      if (fType && r._service_type !== fType) return false;
      if (fStatus && r._status !== fStatus) return false;
      if (dFrom || dTo) {
        const d = r.created_at ? new Date(r.created_at).toLocaleDateString("en-CA") : "";
        if (dFrom && (!d || d < dFrom)) return false;
        if (dTo && (!d || d > dTo)) return false;
      }
      if (tokens.length && !tokens.every(t => r._search.includes(t))) return false;
      return true;
    });
  }, [rows, search, fBy, fClient, fType, fStatus, dFrom, dTo]);

  const hasFilters = !!(search || fBy || fClient || fType || fStatus || dFrom || dTo);
  const clearFilters = () => { setSearch(""); setFBy(""); setFClient(""); setFType(""); setFStatus(""); setDFrom(""); setDTo(""); };

  const inputStyle = {
    background: C.card, border: `1px solid ${C.border}`, borderRadius: 9, color: C.text,
    fontSize: 13, padding: "8px 10px", fontFamily: "'Roboto', sans-serif", outline: "none", colorScheme: "dark",
  };
  const pretty = (s) => s ? String(s).replace(/_/g, " ") : s;
  // "dylan@browning-electric.ca" → "dylan" for compact display.
  const shortBy = (e) => e ? String(e).split("@")[0] : null;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 26, letterSpacing: ".04em", color: C.lime }}>Quotes</div>
          <div style={{ fontSize: 13, color: C.muted }}>Recent generated quotes. Start a new one from an assessment.</div>
        </div>
        <button onClick={() => navigate("/assess/smart/new")}
          style={{ background: C.lime, border: "none", borderRadius: 10, color: "#0d0f12", fontWeight: 700, fontSize: 13, padding: "10px 20px", cursor: "pointer" }}>
          + New Quote
        </button>
      </div>

      {error && <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 12, marginBottom: 14 }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search quotes — client, address, or any word in the quote (e.g. generator)…"
          style={{ ...inputStyle, width: "100%", boxSizing: "border-box", padding: "10px 12px", marginBottom: 10 }}
        />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select value={fBy} onChange={e => setFBy(e.target.value)} style={inputStyle}>
            <option value="">Quoted by — all</option>
            {opts.by.map(v => <option key={v} value={v}>{shortBy(v)}</option>)}
          </select>
          <select value={fClient} onChange={e => setFClient(e.target.value)} style={{ ...inputStyle, maxWidth: 190 }}>
            <option value="">Client — all</option>
            {opts.client.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <select value={fType} onChange={e => setFType(e.target.value)} style={inputStyle}>
            <option value="">Type — all</option>
            {opts.type.map(v => <option key={v} value={v}>{pretty(v)}</option>)}
          </select>
          <select value={fStatus} onChange={e => setFStatus(e.target.value)} style={inputStyle}>
            <option value="">Status — all</option>
            {opts.status.map(v => <option key={v} value={v}>{pretty(v)}</option>)}
          </select>
          <span style={{ color: C.muted, fontSize: 12 }}>Created</span>
          <input type="date" value={dFrom} onChange={e => setDFrom(e.target.value)} style={inputStyle} title="Created from" />
          <span style={{ color: C.muted, fontSize: 12 }}>to</span>
          <input type="date" value={dTo} onChange={e => setDTo(e.target.value)} style={inputStyle} title="Created to" />
          {hasFilters && (
            <button onClick={clearFilters}
              style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 9, color: C.muted, fontSize: 12, padding: "8px 12px", cursor: "pointer" }}>
              Clear ✕
            </button>
          )}
          <span style={{ color: C.muted, fontSize: 12, marginLeft: "auto" }}>
            {loading ? "" : `${filtered.length} of ${rows.length} quotes`}
          </span>
        </div>
      </div>

      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ color: C.muted, textAlign: "left", background: "#11141a" }}>
                {["Client", "Quote", "Address", "By", "Type", "Status", "Total", "Created", "Jobber"].map((h, i) => (
                  <th key={i} style={{ padding: "9px 12px", fontWeight: 600, whiteSpace: "nowrap", borderBottom: `1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} style={{ padding: 18, color: C.muted, textAlign: "center" }}>Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 18, color: C.muted, textAlign: "center" }}>No quotes yet — hit “New Quote”.</td></tr>
              )}
              {!loading && rows.length > 0 && filtered.length === 0 && (
                <tr><td colSpan={9} style={{ padding: 18, color: C.muted, textAlign: "center" }}>No quotes match these filters.</td></tr>
              )}
              {!loading && filtered.map(q => (
                <tr key={q.id} onClick={() => navigate(`/quote/${q.id}`)}
                  style={{ borderBottom: "1px solid #1e2328", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(165,206,58,.05)"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}>
                  <td style={{ padding: "8px 12px", color: C.text, fontWeight: 600, whiteSpace: "nowrap" }}>{q._client_name || "—"}</td>
                  <td style={{ padding: "8px 12px", color: C.text, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={q.project_title || ""}>
                    {q.project_title || "—"}
                  </td>
                  <td style={{ padding: "8px 12px", color: C.muted, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={q._address || ""}>
                    {q._address || "—"}
                  </td>
                  <td style={{ padding: "8px 12px", color: C.muted, whiteSpace: "nowrap" }}>{shortBy(q._quoted_by) || "—"}</td>
                  <td style={{ padding: "8px 12px", color: C.muted, whiteSpace: "nowrap" }}>{pretty(q._service_type) || "—"}</td>
                  <td style={{ padding: "8px 12px", color: C.muted, whiteSpace: "nowrap" }}>{pretty(q._status)}</td>
                  <td style={{ padding: "8px 12px", color: C.lime, fontWeight: 600, whiteSpace: "nowrap" }}>{quoteTotal(q) || "—"}</td>
                  <td style={{ padding: "8px 12px", color: C.muted, whiteSpace: "nowrap" }}>{q.created_at ? new Date(q.created_at).toLocaleDateString("en-CA") : "—"}</td>
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                    {q._jobber_url ? (
                      <a href={q._jobber_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                        style={{ color: "#7dd3fc", fontSize: 12, textDecoration: "none" }} title="Open the request in Jobber">
                        request ↗
                      </a>
                    ) : <span style={{ color: C.muted }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Segment({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      style={{
        background: active ? "rgba(165,206,58,.14)" : "transparent",
        border: `1px solid ${active ? "rgba(165,206,58,.5)" : C.border}`,
        borderRadius: 9, color: active ? C.lime : C.muted, fontWeight: 700, fontSize: 13,
        padding: "8px 16px", cursor: "pointer", fontFamily: "'Roboto', sans-serif",
      }}>
      {children}
    </button>
  );
}

export default function QuotesPage() {
  const perms = usePermissions() || {};
  const isAdminOrOwner = !!(perms.isAdmin || perms.isOwner);
  const [params, setParams] = useSearchParams();

  // Takeoff is for EVERYONE — it's the techs' daily material surface.
  // Saved Templates too: techs save quotes as templates and pick them at Convert.
  const tabs = [{ key: "quotes", label: "Quotes" }, { key: "takeoff", label: "Material Takeoff" }, { key: "saved", label: "Saved Templates" }];
  if (isAdminOrOwner) tabs.push({ key: "backends", label: "Backend Library" }, { key: "templates", label: "Templates" });

  const requested = params.get("view") || "quotes";
  const view = tabs.some(t => t.key === requested) ? requested : "quotes";
  const [backendSub, setBackendSub] = useState(params.get("sub") === "author" ? "author" : "browse");

  const setView = (key) => { const p = new URLSearchParams(params); p.set("view", key); p.delete("sub"); setParams(p, { replace: true }); };

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 16px 8px", color: C.text, fontFamily: "'Roboto', sans-serif" }}>
      {tabs.length > 1 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
          {tabs.map(t => <Segment key={t.key} active={view === t.key} onClick={() => setView(t.key)}>{t.label}</Segment>)}
        </div>
      )}

      {view === "quotes" && <QuotesList />}

      {view === "takeoff" && <MaterialTakeoff />}

      {view === "saved" && <SavedQuoteTemplates />}

      {view === "backends" && isAdminOrOwner && (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <Segment active={backendSub === "browse"} onClick={() => setBackendSub("browse")}>Browse</Segment>
            <Segment active={backendSub === "author"} onClick={() => setBackendSub("author")}>Author / Paste</Segment>
          </div>
          {backendSub === "browse" ? <QuoteBackends /> : <BackendBuilder />}
        </div>
      )}

      {view === "templates" && isAdminOrOwner && <QuoteTemplates />}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Roboto:wght@400;600;700&display=swap');
      `}</style>
    </div>
  );
}
